/**
 * ISO日時文字列の比較
 * @param a ISO日時文字列
 * @param b ISO日時文字列
 * @returns 比較結果（前者の方が未来の場合は正の数値）
 */
export function compareISODatetime(a: string, b: string) {
  return new Date(a).getTime() - new Date(b).getTime();
}

/**
 * 画面表示用日時文字列取得
 * @param datetime ISO日時文字列
 * @returns 画面表示用日時文字列[日付, 時間]
 */
export function displayDatetimeString(datetime: string) {
  if (!datetime || datetime === "") return [null, null];
  try {
    const obj = new Date(datetime);
    const date = obj.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const time = obj
      .toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })
      .slice(0, 5);
    const weekday = getJstWeekday(obj);
    return [`${date}(${weekday})`, time];
  } catch (e) {
    console.warn(e);
    return [null, null];
  }
}

/**
 * 画面表示用日付文字列取得
 * @param ymd 日付文字列（yyyyMMdd）
 * @returns 日付文字列（yyyy/MM/dd）
 */
export function displayDateFromYmd(ymd: string) {
  if (!ymd) return ymd;
  if (ymd.length !== 8) return ymd;
  return `${ymd.slice(0, 4)}/${ymd.slice(4, 6)}/${ymd.slice(6)}`;
}

/**
 * 当日判定
 * @param ymd 判定対象日付（yyyyMMdd）
 * @returns 当日かどうか
 */
export function isToday(ymd: string) {
  if (!ymd) return false;
  if (ymd.length !== 8) return false;
  const today = new Date()
    .toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replaceAll("/", "");
  return today === ymd;
}

/**
 * 当日日付取得
 * @returns 当日日付（yyyy-MM-dd）
 */
export function getJstToday() {
  return new Date()
    .toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replaceAll("/", "-");
}

/**
 * 現在時刻取得
 * @returns 現在時刻（hh:mm）
 */
export function getJstTime() {
  return new Date().toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" }).slice(0, 5);
}

/**
 * inputタグ（type=date）用の日付文字列を取得
 * @param date 対象日時（Dateオブジェクト）
 * @returns 日付文字列
 */
export function getInputDateString(date: Date) {
  return date
    .toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replaceAll("/", "-");
}

/**
 * inputタグ（type=month）用の年月文字列を取得
 * @param date 対象日時（Dateオブジェクト）
 * @returns 年月文字列
 */
export function getInputMonthString(date: Date) {
  return date
    .toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .slice(0, 7)
    .replace("/", "-");
}

/**
 * 今年度取得
 * @returns 今年度（number）
 */
export function getCurrentBusinessYear() {
  const today = getJstToday();
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  return month > 3 ? year : year - 1;
}

/**
 * YMD文字列へ変換
 * @param date Dateオブジェクト
 * @returns JSTのYMD文字列
 */
export function formatYmdString(date: Date) {
  return date
    .toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replaceAll("/", "");
}

/**
 * 日本語表記の曜日を取得
 * @param date Dateオブジェクト
 * @returns 曜日文字（日、月・・・）
 */
export function getJstWeekday(date: Date) {
  const weekday = date.toString().slice(0, 3);
  if (weekday === "Mon") return "月";
  if (weekday === "Tue") return "火";
  if (weekday === "Wed") return "水";
  if (weekday === "Thu") return "木";
  if (weekday === "Fri") return "金";
  if (weekday === "Sat") return "土";
  if (weekday === "Sun") return "日";
  return "";
}
